// 261010 UI2 (review fix #5 + #7) — phân loại CỜ của lượt chạy theo ĐÚNG envelope BE (THUẦN, không antd).
// BE (`filmWorkflowStepKit`): video chưa làm được ⇒ outcome BLOCKED, error.code = BLOCKED_UPSTREAM / BLOCKED_INPUT, NGUYÊN NHÂN ở error.message (NEEDS_SPLIT, NO_PROMPT, GENERATION_CONFIG_MISSING…);
// bản sửa tay ⇒ outcome SKIPPED_MANUAL (error.code = ACTING_MANUAL_EDIT_EXISTS / STORYBOARD_EDITED / PROMPT_EDITED) — là GIỮ sửa tay, KHÔNG phải lỗi.
// `events[].stepFlags[bước]` chỉ mang `code` (= error.code || outcome) ⇒ muốn biết nguyên nhân phải tra run object cùng `subjectKey`.
import type { StepFlag, StepRunObject, WorkflowEventStatus, WorkflowStepKey } from '../../types/filmWorkflow';

export type FlagKind = 'ERROR' | 'KEPT' | 'INFO';
export interface FlagInfo { kind: FlagKind; label: string; hint: string; code: string }
export interface FlagInput { outcome?: string; code?: string; message?: string }

/** Các mã BE dùng khi GIỮ bản sửa tay (không AI). */
export const MANUAL_CODES: readonly string[] = ['SKIPPED_MANUAL', 'ACTING_MANUAL_EDIT_EXISTS', 'STORYBOARD_EDITED', 'PROMPT_EDITED'];
const KEPT_HINT: Record<string, string> = {
  ACTING_MANUAL_EDIT_EXISTS: 'Diễn xuất đã sửa tay — máy giữ nguyên, không ghi đè.',
  STORYBOARD_EDITED: 'Storyboard đã sửa tay — máy giữ nguyên, không ghi đè.',
  PROMPT_EDITED: 'Prompt đã sửa tay — máy giữ nguyên, không ghi đè.',
  SKIPPED_MANUAL: 'Bản đã sửa tay — máy giữ nguyên, không ghi đè.',
};

const OUTCOME_LABEL: Record<string, string> = {
  FAILED: 'lỗi', BLOCKED: 'bị chặn', UNCERTAIN: 'chưa rõ kết quả',
  NOT_RUN_STOP: 'chưa chạy (đã dừng)', NOT_RUN_BUDGET: 'chưa chạy (hết lượt)', NOT_RUN_INTERRUPTED: 'chưa chạy (bị ngắt)',
};
const OUTCOME_HINT: Record<string, string> = {
  FAILED: 'Lượt chạy gặp lỗi.', BLOCKED: 'Thiếu đầu vào để làm — bổ sung rồi chạy lại.',
  UNCERTAIN: 'Chưa biết đã tạo hay chưa — cần đối soát, máy không tự chạy lại.',
  NOT_RUN_STOP: 'Chưa chạy vì đã bấm Dừng.', NOT_RUN_BUDGET: 'Chưa chạy vì hết lượt cho phép.', NOT_RUN_INTERRUPTED: 'Chưa chạy vì lượt chạy bị ngắt.',
};
/** Nguyên nhân chặn (error.code hoặc error.message của BLOCKED_*). */
const CAUSE: Record<string, { label: string; hint: string }> = {
  BLOCKED_UPSTREAM: { label: 'chờ bước trước', hint: 'Bước trước chưa có kết quả cho video này — xử lý bước trước rồi chạy lại.' },
  NEEDS_SPLIT: { label: 'cần chia', hint: 'Video dài quá trần của engine — máy tự chia khi phân đoạn (chặng 1), không cắt giữa câu. Còn thấy cờ này nghĩa là chia không được: chạy lại «Phân đoạn» hoặc sửa tay.' },
  NO_PROMPT: { label: 'chưa có prompt', hint: 'Video chưa có prompt — chạy bước Prompt (hoặc viết tay) trước.' },
  PROMPT_MODE_MISMATCH: { label: 'prompt lệch kiểu', hint: 'Prompt đang lưu khác kiểu của video — đổi kiểu hoặc dựng lại prompt.' },
  NO_DURATION: { label: 'thiếu thời lượng', hint: 'Video chưa có thời lượng — xem lại bước Đạo diễn.' },
  AGENT_EVENT: { label: 'do AI ngoài làm', hint: 'Video này do AI ngoài làm — không tạo clip ở đây.' },
  BLOCKED_INPUT: { label: 'thiếu đầu vào', hint: 'Thiếu đầu vào để làm — bổ sung rồi chạy lại.' },
  GENERATION_CONFIG_MISSING: { label: 'thiếu cấu hình', hint: 'Video chưa chọn engine / độ phân giải — chọn ở ngăn Sửa của video.' },
  GENERATION_CONFIG_INVALID: { label: 'cấu hình hỏng', hint: 'Cấu hình engine của video hỏng — chọn lại ở ngăn Sửa.' },
  CLIP_PENDING: { label: 'clip đang tạo', hint: 'Video đang có clip tạo dở — đợi xong.' },
  CLIP_UNCERTAIN: { label: 'cần đối soát', hint: 'Chưa rõ clip đã tạo chưa — cần đối soát, máy không tự tạo lại.' },
  CLIP_PAID_UNSAVED: { label: 'cần đối soát', hint: 'Clip đã tạo ở nhà cung cấp nhưng chưa lưu được — cần đối soát, máy không tự tạo lại.' },
  CLIP_NOT_SELECTED: { label: 'chưa chọn clip', hint: 'Đã có clip mới nhưng chưa chọn — chọn một bản.' },
};
const BLOCK_CODES = ['BLOCKED_UPSTREAM', 'BLOCKED_INPUT'];
const isToken = (t: string): boolean => /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(t);

export function flagInfoOf(i: FlagInput): FlagInfo {
  const code = String(i.code ?? '');
  const msg = String(i.message ?? '').trim();
  const outcome = String(i.outcome ?? '');
  if (outcome === 'SKIPPED_MANUAL' || MANUAL_CODES.includes(code)) {
    return { kind: 'KEPT', label: 'giữ sửa tay', hint: KEPT_HINT[code] ?? KEPT_HINT.SKIPPED_MANUAL, code: code || outcome };
  }
  // Nguyên nhân thật: BLOCKED_* ⇒ lấy ở message (khi message là mã nguyên nhân đã biết); ngược lại ở code.
  if(['PENDING','RUNNING'].includes(outcome)||['PENDING','RUNNING'].includes(code))return {kind:'INFO',label:outcome==='RUNNING'||code==='RUNNING'?'đang chạy':'chờ',hint:'Đang xử lý lượt hiện tại.',code:code||outcome};
  const cause = BLOCK_CODES.includes(code) && CAUSE[msg] ? msg : code;
  const c = CAUSE[cause];
  if (c) return { kind: 'ERROR', label: c.label, hint: msg && !isToken(msg) && msg !== code ? msg : c.hint, code: cause };
  const key = OUTCOME_LABEL[outcome] ? outcome : OUTCOME_LABEL[code] ? code : /^HTTP_[45][0-9]{2}$/.test(code)?'FAILED':'';
  if(!key)return {kind:'INFO',label:'cần kiểm tra trạng thái',hint:'Tải lại trạng thái.',code:code||outcome};
  return { kind: 'ERROR', label: OUTCOME_LABEL[key], hint: msg && !isToken(msg) ? msg : OUTCOME_HINT[key], code: code || outcome };
}

/** Cờ bền của MỘT video ở một bước (`events[].stepFlags[bước]`), phân loại có tra run object cùng `subjectKey` để biết nguyên nhân thật. */
export function eventFlagInfosOf(step: WorkflowStepKey, event: Pick<WorkflowEventStatus, 'eventId' | 'stepFlags'>, objects?: StepRunObject[]): FlagInfo[] {
  const out: FlagInfo[] = [];
  const seen = new Set<string>();
  for (const f of (event.stepFlags?.[step] ?? []) as StepFlag[]) {
    const o = (objects ?? []).find((x) => x.key === f.subjectKey && (x.eventIds ?? []).includes(event.eventId) && (!(x as any).runId||!f.runId||(x as any).runId===f.runId));
    const info = flagInfoOf({ outcome: o?.outcome, code: f.code, message: o?.error?.message });
    if (seen.has(`${info.kind}|${info.label}`)) continue;
    seen.add(`${info.kind}|${info.label}`); out.push(info);
  }
  return out;
}
